import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  FaUser, FaEnvelope, FaPhone, FaBuilding, FaIdCard,
  FaEdit, FaMapMarkerAlt, FaCheckCircle, FaSpinner, FaLock, FaShieldAlt
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Settings = () => {
  const [profile, setProfile] = useState({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", mobile: "", organization: "" });

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/press/profile`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("pressToken")}` }
      });
      if (res.data.success) {
        const d = res.data.data;
        setProfile(d);
        setForm({ name: d.name || "", email: d.email || "", mobile: d.mobile || "", organization: d.organization || "" });
      } else toast.error("Failed to load profile");
    } catch (err) {
      toast.error("Error loading profile");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchProfile(); }, []);

  const updateProfile = async () => {
    try {
      setSaving(true);
      const res = await axios.put(`${process.env.REACT_APP_API_BASE_URL}/press/profile/update`, form, {
        headers: { Authorization: `Bearer ${localStorage.getItem("pressToken")}` }
      });
      if (res.data.success) {
        setProfile({ ...profile, ...form });
        setEditing(false);
        toast.success("Profile updated!");
      } else toast.error(res.data.message || "Update failed");
    } catch (err) { toast.error("Error updating profile"); }
    finally { setSaving(false); }
  };

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f4f6fb] p-3 sm:p-6 space-y-4">
        <div className="h-32 bg-white rounded-2xl animate-pulse" />
        <div className="h-64 bg-white rounded-2xl animate-pulse" />
      </div>
    );
  }

  const InputField = ({ label, icon, name, type = "text" }) => (
    <div className="bg-slate-50 p-3 sm:p-4 rounded-2xl border border-slate-100 flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center flex-shrink-0 shadow-sm border border-slate-100">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-black text-slate-400 tracking-wider uppercase">{label}</p>
        {editing ? (
          <input type={type} name={name} value={form[name]} onChange={handleChange}
            className="w-full bg-transparent text-sm font-black text-slate-800 outline-none border-b border-transparent focus:border-blue-400 transition-colors py-0.5"
            placeholder={`Enter ${label.toLowerCase()}`} />
        ) : (
          <p className="text-sm font-black text-slate-800 truncate">{profile[name] || "Not provided"}</p>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f4f6fb] p-3 sm:p-6 pb-24">
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />

      {/* Hero Badge */}
      <div className="relative bg-white rounded-[2rem] p-6 mb-4 shadow-sm border border-slate-100 overflow-hidden flex flex-col items-center sm:flex-row sm:items-center sm:justify-start gap-5">
        <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500 opacity-10 blur-3xl rounded-full" />

        <div className="relative w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-tr from-blue-600 to-indigo-500 rounded-3xl p-1 shadow-lg flex-shrink-0">
          <div className="w-full h-full bg-slate-900 rounded-[1.3rem] flex items-center justify-center relative overflow-hidden">
            <span className="text-3xl text-white font-black">{profile.name?.charAt(0) || "P"}</span>
            <div className="absolute inset-0 bg-white/10 mt-10 blur-md"></div>
          </div>
          {profile.isVerified && (
            <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-emerald-500 border-4 border-white rounded-full flex items-center justify-center shadow-sm">
              <FaCheckCircle className="text-white w-3.5 h-3.5" />
            </div>
          )}
        </div>

        <div className="relative z-10 text-center sm:text-left flex-1 min-w-0">
          <h1 className="text-2xl font-black text-slate-800 leading-tight truncate">{profile.name || "Press User"}</h1>
          <p className="text-sm font-medium text-slate-500 mb-2 truncate">{profile.email}</p>
          <div className="flex items-center justify-center sm:justify-start gap-2 flex-wrap">
            <span className={`text-[10px] font-black px-2.5 py-1 rounded-md uppercase tracking-wider ${profile.isVerified ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`}>
              {profile.isVerified ? "Verified Account" : "Pending Verification"}
            </span>
            <span className="text-[10px] font-black px-2.5 py-1 rounded-md uppercase tracking-wider bg-blue-50 text-blue-600 flex items-center gap-1">
              <FaIdCard /> ID: {profile.pressConferenceId || "N/A"}
            </span>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-[2rem] shadow-sm border border-slate-100 p-4 sm:p-6 mb-4 relative overflow-hidden">
        {/* Header w Action */}
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-black text-slate-800">Personal Info</h2>
          <button onClick={() => { if (editing) { setForm({ name: profile.name || "", email: profile.email || "", mobile: profile.mobile || "", organization: profile.organization || "" }); } setEditing(!editing); }}
            className={`text-xs font-black px-4 py-2 rounded-xl transition-all active:scale-95 flex items-center gap-1.5 ${editing ? "bg-slate-100 text-slate-600" : "bg-blue-50 text-blue-600 hover:bg-blue-100"}`}>
            <FaEdit /> {editing ? "Cancel" : "Edit"}
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <InputField label="Full Name" icon={<FaUser className="text-blue-500" />} name="name" />
          <InputField label="Email Address" icon={<FaEnvelope className="text-indigo-500" />} name="email" type="email" />
          <InputField label="Mobile Number" icon={<FaPhone className="text-emerald-500" />} name="mobile" type="tel" />
          <InputField label="Organization" icon={<FaBuilding className="text-amber-500" />} name="organization" />
        </div>

        {/* Action Button Strip */}
        {editing && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <button onClick={updateProfile} disabled={saving}
              className="w-full sm:w-auto px-6 py-3 bg-blue-600 text-white text-sm font-black rounded-xl hover:bg-blue-700 active:scale-95 transition-all flex items-center justify-center gap-2">
              {saving ? <FaSpinner className="animate-spin" /> : <FaCheckCircle />}
              {saving ? "Saving Changes..." : "Save Changes"}
            </button>
          </div>
        )}
      </div>

      {/* Security & System Info Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-white rounded-[1.5rem] shadow-sm border border-slate-100 p-5 flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center flex-shrink-0">
            <FaLock className="text-slate-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-black text-slate-800">Security</h3>
            <p className="text-xs text-slate-500 font-medium mt-1 mb-3">Manage your password and authentication.</p>
            <button className="text-[10px] font-black bg-slate-100 text-slate-600 px-3 py-1.5 rounded-lg active:scale-95 transition-all">
              Change Password
            </button>
          </div>
        </div>

        <div className="bg-white rounded-[1.5rem] shadow-sm border border-slate-100 p-5 flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center flex-shrink-0">
            <FaShieldAlt className="text-slate-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-black text-slate-800">Account Status</h3>
            <div className="mt-2 space-y-1">
              <div className="flex justify-between items-center bg-slate-50 p-1.5 rounded-lg">
                <span className="text-[10px] font-bold text-slate-400 uppercase">Joined</span>
                <span className="text-[10px] font-black text-slate-700">
                  {profile.createdAt ? new Date(profile.createdAt).toLocaleDateString("en-IN", { month: "short", year: "numeric" }) : "N/A"}
                </span>
              </div>
              <div className="flex justify-between items-center bg-slate-50 p-1.5 rounded-lg">
                <span className="text-[10px] font-bold text-slate-400 uppercase">Type</span>
                <span className="text-[10px] font-black text-slate-700">Press Conference</span>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default Settings;
